import React, { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import type {
  AiCompanionSessionContext,
  SaveOnboardingDetailsFields,
} from '@pharmaplan/common';

import CustomRadioTable from '../../common/CustomRadioTable';

type SoftwareOptions = NonNullable<
  AiCompanionSessionContext['softwareOptions']
>;
type SoftwareSelections = SaveOnboardingDetailsFields['selectedSoftwares'];
type SoftwareExperienceValues = Record<string, number>;

interface IAiSoftwareExperienceTable {
  onChange: (selectedSoftwares: SoftwareSelections) => void;
  selectedSoftwares: SoftwareSelections;
  softwareOptions: SoftwareOptions;
}

const getExperienceLevel = (value: unknown): 1 | 2 | 3 | 4 | 5 => {
  const level = Number(value);

  return level >= 1 && level <= 5
    ? level as 1 | 2 | 3 | 4 | 5
    : 1;
};

const buildSoftwareExperienceValues = (
  softwareOptions: SoftwareOptions,
  selectedSoftwares: SoftwareSelections,
): SoftwareExperienceValues =>
  softwareOptions.reduce<SoftwareExperienceValues>((values, { softwareId }) => {
    const experience = selectedSoftwares.find((software) =>
      software.softwareId === softwareId)?.expLevel ?? 1;

    values[softwareId] = experience;

    return values;
  }, {});

const buildSoftwareSelections = (
  softwareOptions: SoftwareOptions,
  values: SoftwareExperienceValues,
): SoftwareSelections =>
  softwareOptions.map(({ softwareId }) =>
    ({
      expLevel: getExperienceLevel(values[softwareId]),
      softwareId,
    }));

const hasSameSoftwareSelections = (
  first: SoftwareSelections,
  second: SoftwareSelections,
) =>
  first.length === second.length
  && first.every(({ expLevel, softwareId }, index) =>
    expLevel === second[index]?.expLevel
    && softwareId === second[index]?.softwareId);

const handleSubmit = () =>
  Promise.resolve();

const AiSoftwareExperienceTable = ({
  onChange,
  selectedSoftwares,
  softwareOptions,
}: IAiSoftwareExperienceTable) => {
  const initialValues = useMemo(
    () =>
      buildSoftwareExperienceValues(softwareOptions, selectedSoftwares),
    [selectedSoftwares, softwareOptions],
  );
  const formik = useFormik<SoftwareExperienceValues>({
    enableReinitialize: true,
    initialValues,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const nextSelections = buildSoftwareSelections(
      softwareOptions,
      formik.values,
    );

    if (!hasSameSoftwareSelections(selectedSoftwares, nextSelections)) {
      onChange(nextSelections);
    }
  }, [formik.values, onChange, selectedSoftwares, softwareOptions]);

  return (
    <CustomRadioTable
      formik={formik}
      isSignup
      softwareOptions={softwareOptions}
    />
  );
};

export default AiSoftwareExperienceTable;
